import {http} from '../http'
import * as corePhotos from '../../coreApi/photos'

import {APIResponse} from '../types'
import {PhotoPersonTag} from '../../../types'
import {handleError} from '../handleError'

interface Params {
  clientId: number
  photoId: number
}

type ListPhotoPersonTagsReturn = APIResponse<PhotoPersonTag[]>

const ERROR_PATH = 'api.photos.listPhotoPersonTags()'

/**
 * The single source of the user-facing failure copy for this op. Exported
 * because `usePhotoPersonTags` re-applies it rather than forwarding
 * `error.message`: `handleError` substitutes the SERVER's `response.data.message`
 * for any 4xx, and this view must stay generic (AB#23610 plan, D3). Two
 * byte-identical copies of the sentence would drift, so both layers read this.
 */
export const PHOTO_TAGS_GENERIC_ERROR =
  'Something went wrong while loading photo tags. Please try again.'

/**
 * Every person tag on one photo, decoded to the camelCase domain shape.
 *
 * The wire body is PascalCase (`{"Results":[{"PersonID":…}]}`) — decoded against
 * the photos Go source, NOT inferred from the adjacent getAlbumTagStats op,
 * whose hand-authored struct is camelCase. Two ops, two casings, same service.
 *
 * An empty `Results` is a SUCCESS with an empty array, never a failure and never
 * 'notfound': the service answers an untagged photo with 200 `{"Results":[]}`,
 * and surfacing "nobody is tagged yet" is the feature's primary use case.
 *
 * Takes numeric ids because every caller on this path is already numeric,
 * having parsed and NaN-guarded once at the gallery boundary.
 */
export const listPhotoPersonTags = async ({
  clientId,
  photoId,
}: Params): Promise<ListPhotoPersonTagsReturn> => {
  try {
    const response = await corePhotos.listPhotoPersonTags(http, {
      ClientID: clientId,
      PhotoID: photoId,
    })

    // `Results` is guaranteed non-null by the service (its repository seeds a
    // non-nil empty slice), so this coalesce only defends against a malformed
    // or truncated body rather than a documented shape.
    const results = response?.data?.Results ?? []

    return {
      result: 'success',
      data: results.map((tag) => ({
        personId: tag.PersonID,
        photoId: tag.PhotoID,
        manual: tag.Manual,
        confidence: tag.Confidence ?? null,
        taggingPersonId: tag.TaggingPersonID ?? null,
      })),
    }
  } catch (e) {
    return handleError(ERROR_PATH, PHOTO_TAGS_GENERIC_ERROR, e)
  }
}
